import { useEffect, useMemo } from "react";
import { useForm, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  FORM_ERROR_DELAY_IN_MS,
  COVERTREE_EMAIL_ADDRESS,
  SALES_EMAIL_ADDRESS,
} from "@covertree/consts";
import { ContinueQuoteForm, continueQuoteValidation } from "./validation";

interface UseContinueQuoteFormProps {
  defaultEffectiveDate?: string;
  isInternalAgent: boolean;
  onSubmit: (data: ContinueQuoteForm) => void;
}

export const useContinueQuoteForm = ({
  defaultEffectiveDate,
  isInternalAgent,
  onSubmit,
}: UseContinueQuoteFormProps) => {
  const emailAddress = useMemo(
    () => (isInternalAgent ? COVERTREE_EMAIL_ADDRESS : SALES_EMAIL_ADDRESS),
    [isInternalAgent]
  );

  const disclaimerMessage = useMemo(
    () =>
      defaultEffectiveDate
        ? "The effective date is prefilled from the quote. You may adjust it by up to 7 days if needed."
        : "The effective date cannot be earlier than today (ET time zone) or later than 30 days from today.",
    [defaultEffectiveDate]
  );

  const form = useForm<ContinueQuoteForm>({
    mode: "onChange",
    defaultValues: {
      effectiveDate: defaultEffectiveDate ?? undefined,
    },
    delayError: FORM_ERROR_DELAY_IN_MS,
    shouldUnregister: true,
    resolver: yupResolver(
      continueQuoteValidation(emailAddress, defaultEffectiveDate)
    ) as Resolver<ContinueQuoteForm>,
    reValidateMode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { isValid },
  } = form;

  useEffect(() => {
    reset({
      effectiveDate: defaultEffectiveDate ?? undefined,
    });
  }, [defaultEffectiveDate, reset]);

  return {
    form,
    handleSubmit: handleSubmit(onSubmit),
    control,
    isValid,
    emailAddress,
    disclaimerMessage,
  };
};
